import { styled } from "@linaria/react";
import React from "react";
import type { DropzoneOptions, DropzoneProps } from "react-dropzone";
import { useDropzone } from "react-dropzone";
import { color } from "./..";
import t from "../i18n";
import Button from "../Button";
import { UploadLine } from "../IconV2/react";
import Spinner from "../Spinner";

const Wrapper = styled.div`
  cursor: pointer;
  padding: 12px 32px 6px 32px;
  outline: none;
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const Svg = styled.svg`
  color: ${color.carbon800};
  height: 64px;
  width: 64px;
  margin-bottom: 16px;
`;

const Text = styled.p`
  margin-bottom: 24px;
`;

const Input = styled.input``;
const InactiveWording = styled.div`
  text-align: center;
`;

interface P {
  onDrop: DropzoneProps["onDrop"];
  accept?: DropzoneProps["accept"];
  maxFiles?: DropzoneProps["maxSize"];
  maxSize?: DropzoneProps["maxSize"];
  multiple?: DropzoneProps["multiple"];
  isLoading?: DropzoneProps["disabled"];
}

const Dropzone = ({ onDrop, accept, maxFiles, maxSize, multiple, isLoading }: P) => {
  const [loading, setLoading] = React.useState(false);
  if (!onDrop) throw new Error("missing onDrop");
  const handleDrop: typeof onDrop = React.useCallback(
    (...args) => {
      setLoading(loading => {
        if (loading) throw new Error("Already loading");
        return true;
      });

      try {
        onDrop(...args);
      } finally {
        setLoading(false);
      }
    },
    [onDrop],
  );

  const options: DropzoneOptions = React.useMemo(
    () => ({
      onDrop: handleDrop,
      disabled: isLoading ?? loading,
      accept,
      maxFiles,
      maxSize,
      multiple,
    }),
    [handleDrop, isLoading, loading, accept, maxFiles, maxSize, multiple],
  );

  const dropzone = useDropzone(options);
  const style = React.useMemo(
    () => ({
      border: dropzone.isDragActive
        ? `2px dashed ${color.raw.lightNitrogen500_Main}`
        : "2px solid white",
    }),
    [dropzone.isDragActive],
  );
  const insideStyle = React.useMemo(
    () => ({
      opacity: dropzone.isDragActive ? 0.5 : 1,
    }),
    [dropzone.isDragActive],
  );

  return (
    <Wrapper {...dropzone.getRootProps()} data-cy="dropzone" style={style}>
      <Input {...dropzone.getInputProps()} />
      <InactiveWording style={insideStyle}>
        <Svg as={(isLoading ?? loading) ? Spinner : UploadLine} />
        <Text>
          {t("dropzone.drop_here", {
            oneFile: multiple ? false : true,
          })}
        </Text>
        <Button type="button" disabled={loading} variant="secondary">
          {t("dropzone.select_files", {
            oneFile: multiple ? false : true,
          })}
        </Button>
      </InactiveWording>
    </Wrapper>
  );
};

export default React.memo(Dropzone);
